import { TableCell, TableRow } from '@/components/ui/table';
import { EnabledBadge } from './StatusBadge';
import { formatCurrency, formatPercentage } from './utils';
import { ReactNode } from 'react';

interface FeeBreakdownItem {
  count: number;
  volume?: number;
  unitPrice: number;
  percentageRate?: number;
  percentageTotal?: number;
  total: number;
  enabled: boolean;
}

interface FeeBreakdownRowProps {
  label: string;
  icon: ReactNode;
  categoryTag?: string;
  item: FeeBreakdownItem;
  className?: string;
  showUnitPrice?: boolean;
  unitPriceSuffix?: string;
  /**
   * Optional one-line explanatory subtitle shown below the label.
   * Used to surface conditional-activation context (e.g. "Activates
   * from 1 approved transaction this period (currently 0)") so the
   * merchant immediately understands an "Inactive" status that's
   * really just "pending activity" rather than a contractual opt-out.
   */
  subtitle?: ReactNode;
}

export function FeeBreakdownRow({
  label,
  icon,
  categoryTag,
  item,
  className = 'border-b hover:bg-muted/50',
  showUnitPrice = true,
  unitPriceSuffix = '',
  subtitle
}: FeeBreakdownRowProps) {
  const fixedFee = (item.unitPrice * item.count) / 100;
  const percentageFee = (item.percentageTotal || 0) / 100;

  return (
    <TableRow className={className}>
      <TableCell className="py-3 px-4">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-medium">{label}</span>
            {categoryTag && (
              <span className="text-xs text-muted-foreground">{categoryTag}</span>
            )}
          </div>
          {subtitle && (
            <span className="text-xs text-muted-foreground pl-6">{subtitle}</span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right px-2 font-medium">
        {item.count.toLocaleString()}
      </TableCell>
      <TableCell className="text-right px-2 font-medium">
        {item.volume !== undefined ? `$${formatCurrency(item.volume)}` : '-'}
      </TableCell>
      <TableCell className="text-right px-2 border-l-2 border-blue-100 text-blue-700">
        {showUnitPrice ? `$${(item.unitPrice / 100).toFixed(2)}${unitPriceSuffix}` : '-'}
      </TableCell>
      <TableCell className="text-right px-2 text-blue-700">
        {item.percentageRate !== undefined ? formatPercentage(item.percentageRate) : '-'}
      </TableCell>
      <TableCell className="text-right px-2 border-l-2 border-green-100 text-green-700">
        {showUnitPrice ? `$${formatCurrency(fixedFee * 100)}` : '-'}
      </TableCell>
      <TableCell className="text-right px-2 text-green-700">
        {item.percentageRate !== undefined ? `$${formatCurrency(item.percentageTotal || 0)}` : '-'}
      </TableCell>
      <TableCell className="text-right px-2 font-bold border-l-2 border-gray-200">
        ${formatCurrency(item.total)}
      </TableCell>
      <TableCell className="text-center px-2">
        <EnabledBadge enabled={item.enabled} />
      </TableCell>
    </TableRow>
  );
}
